import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X } from 'lucide-react';
import { supabase } from '../../lib/db.ts';
import { logger } from '../../services/logger.ts';

interface FAQ {
  id: string;
  title: string;
  content: string;
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function FAQsManager() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_faqs');

      if (error) throw error;
      setFaqs(data || []);
    } catch (err) {
      logger.error('Error fetching FAQs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFaq = async () => {
    try {
      const { error } = await supabase
        .rpc('add_faq', {
          p_title: newFaq.question,
          p_content: newFaq.answer
        });

      if (error) throw error;

      await fetchFaqs(); // Refresh the list to get the new FAQ
      setNewFaq({ question: '', answer: '' });
      setIsAdding(false);
    } catch (err) {
      logger.error('Error adding FAQ:', err);
    }
  };

  const handleUpdateFaq = async (id: string, question: string, answer: string) => {
    try {
      const { error } = await supabase
        .rpc('update_faq', {
          p_id: id,
          p_title: question,
          p_content: answer
        });

      if (error) throw error;

      await fetchFaqs(); // Refresh the list to get the updated FAQ
      setIsEditing(null);
    } catch (err) {
      logger.error('Error updating FAQ:', err);
    }
  };

  const handleDeleteFaq = async (id: string) => {
    try {
      const { error } = await supabase
        .rpc('delete_faq', {
          p_id: id
        });

      if (error) throw error;

      await fetchFaqs(); // Refresh the list to get the updated order
    } catch (err) {
      logger.error('Error deleting FAQ:', err);
    }
  };

  const handleReorder = async (id: string, newOrder: number) => {
    try {
      const { error } = await supabase
        .rpc('reorder_faqs', {
          p_id: id,
          p_new_order: newOrder
        });

      if (error) throw error;

      await fetchFaqs(); // Refresh the list to get the new order
    } catch (err) {
      logger.error('Error reordering FAQ:', err);
    }
  };

  const handleToggleVisibility = async (id: string) => {
    try {
      const { error } = await supabase
        .rpc('toggle_faq_visibility', {
          p_id: id
        });

      if (error) throw error;

      await fetchFaqs(); // Refresh the list to get the updated visibility
    } catch (err) {
      logger.error('Error toggling FAQ visibility:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Manage FAQs</h2>
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center gap-2 px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-wayja-teal/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add FAQ
        </button>
      </div>

      {isAdding && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Add New FAQ</h3>
            <button
              onClick={() => setIsAdding(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question
              </label>
              <input
                type="text"
                value={newFaq.question}
                onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                placeholder="Enter the question"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Answer
              </label>
              <textarea
                value={newFaq.answer}
                onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                rows={4}
                placeholder="Enter the answer"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsAdding(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddFaq}
                className="px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-wayja-teal/90 transition-colors"
              >
                Save FAQ
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div
            key={faq.id}
            className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
          >
            {isEditing === faq.id ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question
                  </label>
                  <input
                    type="text"
                    value={faq.title}
                    onChange={(e) => {
                      setFaqs(faqs.map(f => 
                        f.id === faq.id ? { ...f, title: e.target.value } : f
                      ));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Answer
                  </label>
                  <textarea
                    value={faq.content}
                    onChange={(e) => {
                      setFaqs(faqs.map(f => 
                        f.id === faq.id ? { ...f, content: e.target.value } : f
                      ));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                    rows={4}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setIsEditing(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleUpdateFaq(faq.id, faq.title, faq.content)}
                    className="px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-wayja-teal/90 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">{faq.title}</h3>
                    <button
                      onClick={() => handleToggleVisibility(faq.id)}
                      className={`px-2 py-1 text-xs rounded ${
                        faq.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {faq.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsEditing(faq.id)}
                      className="p-1 text-gray-500 hover:text-gray-700"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteFaq(faq.id)}
                      className="p-1 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <p className="text-gray-600 whitespace-pre-wrap">{faq.content}</p>
                <div className="mt-4 flex gap-2">
                  {index > 0 && (
                    <button
                      onClick={() => handleReorder(faq.id, index)}
                      className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                    >
                      Move Up
                    </button>
                  )}
                  {index < faqs.length - 1 && (
                    <button
                      onClick={() => handleReorder(faq.id, index + 2)}
                      className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                    >
                      Move Down
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
} 